import * as React from 'react'
import * as Dialog from 'primitives/dialog'
import { IconButton } from 'primitives/buttons/icon-button'
import { XCloseIcon } from 'primitives/icons'
import { PaymentStatus } from '../../gen/types'
import { Z_INDEX_MODALS } from 'constants/modals'
import { useLangCookie } from 'cf-i18n/cookies'
import { defaultLanguage } from 'cf-i18n/constants'
import i18n from './i18n/i18n.json'
import { toSupportedLanguage } from '../../i18n/supported-language'

export interface PaymentFailedModalProps {
  paymentStatus: PaymentStatus | null
  message: string | null
  onSettled?: () => void
  closeModal: () => void
}

export function PaymentFailedModal({
  paymentStatus,
  message,
  onSettled,
  closeModal,
}: PaymentFailedModalProps) {
  const [langCookie] = useLangCookie()
  const locale = langCookie || defaultLanguage
  const t = i18n[toSupportedLanguage(locale)]

  return (
    <Dialog.Root
      open={Boolean(paymentStatus && paymentStatus !== PaymentStatus.Succeeded)}
      onOpenChange={() => {
        if (onSettled) {
          onSettled()
        }
        closeModal()
      }}
      defaultOpen={false}
    >
      <Dialog.Portal>
        <div data-cf-external>
          <Dialog.Body dismissable={false} zIndex={Z_INDEX_MODALS.oneClickUpgrade.modal}>
            <div className="font-poppins relative flex w-full max-w-[450px] flex-col items-center rounded-[12px] bg-[#ffffff] p-5 text-[#393D56]">
              {paymentStatus === PaymentStatus.Processing ? (
                //payment processing
                <h3 className="text-center text-[16px] font-bold">
                  {t.messages.processing}&nbsp;
                  {message ? (
                    <a className="underline" target="_blank" href={message || ''} rel="noreferrer">
                      {t.messages.here}
                    </a>
                  ) : null}
                </h3>
              ) : null}

              {paymentStatus === PaymentStatus.RequiresAction ? (
                //Requires action
                <h3 className="flex flex-col text-center text-[16px] font-bold">
                  {message ? t.messages.requires_action : t.messages.upgrade_failed}
                  {message ? (
                    <a className="underline" target="_blank" href={message || ''} rel="noreferrer">
                      {t.messages.cta}
                    </a>
                  ) : null}
                </h3>
              ) : null}

              {paymentStatus === PaymentStatus.Failed ? (
                //payment failed
                <h3 className="flex flex-col text-center text-[16px] font-bold">
                  {message ? (
                    <>
                      {t.messages.upgrade_failed_1}
                      <a
                        className="underline"
                        target="_blank"
                        href={message || ''}
                        rel="noreferrer"
                      >
                        {t.messages.cta}
                      </a>
                    </>
                  ) : (
                    <>
                      {t.messages.invalid_payment}
                      <a
                        className="underline"
                        target="_blank"
                        href={'/my-account/payment-methods/'}
                        rel="noreferrer"
                      >
                        {t.messages.click_here}
                      </a>
                    </>
                  )}
                </h3>
              ) : null}

              <div className="absolute right-[-18px] top-[-18px] [@media(min-width:1340px)]:-top-[52px] [@media(min-width:1340px)]:translate-y-full">
                <Dialog.Close asChild>
                  <IconButton
                    variant="violet"
                    className="h-[36px] w-[36px] rounded-[50%] transition-transform hover:scale-110 active:scale-100"
                  >
                    <img
                      src={XCloseIcon.src}
                      width="30"
                      height="30"
                      className="h-[30px] w-[30px] [filter:invert(1)]"
                    />
                  </IconButton>
                </Dialog.Close>
              </div>
            </div>
          </Dialog.Body>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
